/*
 * The one email composer's frame (docs/2026-10-23-unified-composer-design.md). Reply, Reply all,
 * follow-up and Forward are presets of the same email: each fills it in differently and none draws it
 * differently (Dawid, 2026-10-23: "the reply + reply to all + forward are the default states of the
 * same email component"). Its parts, top to bottom:
 *
 * - the head: the mode as a menu (Gmail's reply-type arrow), whose message it answers, and the fold;
 * - the fields: To, Cc, Bcc, From and Subject on the sheet's own lines, labels in `muted`, values
 *   inline with no box; a field the route cannot change is the same field, read-only, saying why
 *   (`compose-capabilities.ts`);
 * - the body and what it quotes, the files, the notices (drawn by each mode's owner);
 * - the foot: the format switch, quiet, at its left; the status line; Attach and Send at its right.
 */
import { type ReactNode, useId, useState } from "react";
import { Segmented } from "../drafting-switches.tsx";
import { ChevronDownIcon, ForwardArrowIcon, ReplyAllArrowIcon, ReplyArrowIcon } from "../icons.tsx";
import { OptionMenu } from "../option-menu.tsx";
import { Tip } from "../tip.tsx";
import type { FieldCapability } from "./compose-capabilities.ts";

export type ComposeMode = "reply" | "reply-all" | "forward";
type Format = "text" | "html";

const MODE_WORDS: Record<ComposeMode, string> = {
  reply: "Reply",
  "reply-all": "Reply all",
  forward: "Forward",
};

const modeIcon = (mode: ComposeMode) =>
  mode === "forward" ? (
    <ForwardArrowIcon size={16} />
  ) : mode === "reply-all" ? (
    <ReplyAllArrowIcon size={16} />
  ) : (
    <ReplyArrowIcon size={16} />
  );

/** The same switch in every mode, same order, its chosen word in ink and the other muted. */
const FORMATS = [
  { value: "text", label: "Plain text" },
  { value: "html", label: "Formatted" },
] as const;

export function FormatSwitch(props: {
  value: Format;
  disabled: boolean;
  onChange: (format: Format) => void;
}) {
  return (
    <Segmented
      className="lcr-i2-format"
      label="Format"
      value={props.value}
      options={FORMATS}
      disabled={props.disabled}
      onChange={props.onChange}
    />
  );
}

/**
 * The head: the mode, as a menu where the message offers another, then whose message this answers
 * or forwards, then the fold, which keeps everything written (Esc does the same).
 */
export function ComposerHead(props: {
  mode: ComposeMode;
  /** What the mode reads as when it is the bottom box's own: "Follow up". */
  word?: string;
  modes: readonly ComposeMode[];
  onMode: (mode: ComposeMode) => void;
  source: string | null;
  onFold: (() => void) | null;
}) {
  const word = props.word ?? MODE_WORDS[props.mode];
  // Esc in the open menu closes the menu only; the sheet's own Esc folds the whole composer.
  const [menu, setMenu] = useState(false);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: keeps the menu's Esc from folding the sheet
    <div
      className="lcr-i2-compose-head"
      onKeyDown={(event) => {
        if (event.key === "Escape" && menu) event.stopPropagation();
      }}
    >
      {props.modes.length > 1 ? (
        <OptionMenu
          open={menu}
          onOpenChange={setMenu}
          label="Reply type"
          className="lcr-i2-compose-mode"
          icon={modeIcon(props.mode)}
          value={props.mode}
          display={word}
          options={props.modes.map((mode) => ({
            value: mode,
            label: MODE_WORDS[mode],
            mark: modeIcon(mode),
          }))}
          onChange={props.onMode}
        />
      ) : (
        <span className="lcr-i2-compose-word">
          {modeIcon(props.mode)}
          {word}
        </span>
      )}
      {props.source && <span className="lcr-i2-compose-source">{props.source}</span>}
      {props.onFold && (
        <span className="lcr-i2-mail-action lcr-i2-compose-fold">
          <button
            type="button"
            className="lcr-icon-button"
            aria-label="Close. What you wrote is kept."
            aria-keyshortcuts="Escape"
            onClick={props.onFold}
          >
            <ChevronDownIcon size={18} />
          </button>
          <Tip className="lcr-i2-mail-tip">Close (Esc)</Tip>
        </span>
      )}
    </div>
  );
}

/**
 * One of the email's fields on its own line: a label and the value inline. Editable, `children`
 * is the input; read-only, the value is drawn as the same field, focusable, with the reason as its
 * description: a tip on a desktop, a line under it on a tap on a phone.
 */
export function FieldRow(props: {
  label: string;
  capability: FieldCapability;
  /** The input's id, so the label names it; a read-only field makes its own. */
  inputId?: string;
  /** The value of a read-only field. */
  value?: string;
  children?: ReactNode;
  /** Controls at the line's end: "Cc" and "Bcc" on the To line. */
  end?: ReactNode;
  /** Leaving a read-only field: an empty Cc or Bcc line folds back to its link. */
  onBlur?: () => void;
}) {
  const own = useId();
  const id = props.inputId ?? own;
  const note = `${id}-why`;
  const locked = !props.capability.editable;
  return (
    <div className="lcr-i2-field" data-locked={locked || undefined}>
      <label className="lcr-i2-field-label" htmlFor={id}>
        {props.label}
      </label>
      <span className="lcr-i2-field-value">
        {locked ? (
          <input
            id={id}
            className="lcr-i2-field-input"
            readOnly
            value={props.value ?? ""}
            aria-describedby={note}
            onBlur={props.onBlur}
          />
        ) : (
          props.children
        )}
        {!props.capability.editable && (
          <span className="lcr-i2-field-why" id={note} role="note">
            {props.capability.reason}
          </span>
        )}
      </span>
      {props.end && <span className="lcr-i2-field-end">{props.end}</span>}
    </div>
  );
}

/** "Cc" or "Bcc" at the end of the To line: adds its line and puts the caret in it. */
export function RevealField(props: { label: string; onReveal: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className="lcr-linklike lcr-i2-field-reveal"
      aria-label={`Add ${props.label}`}
      disabled={props.disabled}
      onClick={props.onReveal}
    >
      {props.label}
    </button>
  );
}

/**
 * The foot: the format switch and its tools at the left, the one line of status, the actions at
 * the right. `notice` sits over them all, just above Send: a moved thread's "New since you started".
 */
export function ComposerFoot(props: {
  format: ReactNode;
  tools?: ReactNode;
  status: ReactNode;
  actions: ReactNode;
  notice?: ReactNode;
}) {
  return (
    <div className="lcr-compose-foot lcr-i2-reply-foot">
      {props.notice ? <div className="lcr-i2-compose-notice">{props.notice}</div> : null}
      <div className="lcr-i2-foot-tools">
        {props.format}
        {props.tools}
      </div>
      {props.status}
      <div className="lcr-i2-reply-actions">{props.actions}</div>
    </div>
  );
}
